import {issuePath} from '@github-ui/paths'
import {createIssueFieldValueMutation as createIssueFieldValueQueryId} from '@github-ui/server-persisted-queries'
import type {CreateIssueFieldValueMutationMutation} from '@github-ui/server-persisted-queries/types/createIssueFieldValueMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'

import type {IssueFieldValueMutationInput} from './helpers'

const CREATE_ERROR = 'Create issue field value failed'
type ConfirmedIssueFieldValue = NonNullable<
  NonNullable<CreateIssueFieldValueMutationMutation['createIssueFieldValue']>['issueFieldValue']
>
type CreateResponse = {data?: CreateIssueFieldValueMutationMutation | null; errors?: readonly unknown[] | null}

export async function executeNativeCreateIssueFieldValueMutation(
  owner: string,
  repo: string,
  number: number,
  input: IssueFieldValueMutationInput,
): Promise<ConfirmedIssueFieldValue> {
  const query = new URLSearchParams({queryId: createIssueFieldValueQueryId})
  const path = `${issuePath({owner, repo, issueNumber: number})}/field_value.json`
  const response = await reactFetchJSON(`${path}?${query.toString()}`, {
    method: 'POST',
    body: {issueField: input.issueField},
  })
  if (!response.ok) throw new Error(`${CREATE_ERROR} (${response.status})`)

  let json: CreateResponse
  try {
    json = (await response.json()) as CreateResponse
  } catch {
    throw new Error(`${CREATE_ERROR}: response was invalid`)
  }
  const message = json.errors?.length
    ? json.errors
        .flatMap(error =>
          error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
            ? [error.message]
            : [],
        )
        .join(', ')
    : undefined
  if (!json.data) throw new Error(message || `${CREATE_ERROR}: response data was missing`)

  const payload = json.data.createIssueFieldValue
  if (!payload) throw new Error(message || `${CREATE_ERROR}: payload was missing`)
  if (!payload.issue) throw new Error(message || `${CREATE_ERROR}: issue was missing`)
  if (payload.issue.id !== input.issueId) throw new Error(message || `${CREATE_ERROR}: issue did not match`)
  if (!payload.issueFieldValue) throw new Error(message || `${CREATE_ERROR}: issue field value was missing`)

  return payload.issueFieldValue
}
